import { useMemo, useState } from "react";

import {
  AccountPanelHeading,
  AccountTableFrame,
  ColumnHelp,
  SortHeading,
  accountBodyCellClass,
  accountHeadCellClass,
  compareNullableSortValues,
  count,
  navigatorNames,
  percent,
} from "./accountManagementShared";
import type { DataCoverageClinic, DataCoverageResponse } from "./accountManagementTypes";
import { DashboardFilterField, DashboardFilterRow, DashboardSelect } from "./dashboardKit";
import { PipelineCoveragePanel } from "./accountManagementPipelineCoverage";

type CoverageMetric = {
  key:
    | "mbiCoverageRate"
    | "dobCoverageRate"
    | "phoneCoverageRate"
    | "icd10CoverageRate"
    | "providerCoverageRate"
    | "npiCoverageRate"
    | "pverifyCoverageRate"
    | "allDataCoverageRate";
  label: string;
  definition: string;
  countKey:
    | "mbiCoveredPatientCount"
    | "dobCoveredPatientCount"
    | "phoneCoveredPatientCount"
    | "icd10CoveredPatientCount"
    | "providerCoveredPatientCount"
    | "npiCoveredPatientCount"
    | "pverifyCoveredPatientCount"
    | "allDataCoveredPatientCount";
};

const COVERAGE_METRICS: CoverageMetric[] = [
  {
    key: "mbiCoverageRate",
    label: "MBI coverage",
    definition:
      "Patients with an MBI in their patient record, linked CRM referral, or BCDA patient data.",
    countKey: "mbiCoveredPatientCount",
  },
  {
    key: "dobCoverageRate",
    label: "DoB coverage",
    definition:
      "Patients with a date of birth in their patient record, linked CRM referral, or BCDA patient data.",
    countKey: "dobCoveredPatientCount",
  },
  {
    key: "phoneCoverageRate",
    label: "Phone coverage",
    definition:
      "Patients with an active CareSpace phone or linked CRM referral phone containing at least 10 digits.",
    countKey: "phoneCoveredPatientCount",
  },
  {
    key: "icd10CoverageRate",
    label: "ICD-10 coverage",
    definition:
      "Patients with a nonempty diagnosis code in PAAF, recorded clinical visits (including future appointments), linked CRM clinical snapshots, or linked BCDA claims.",
    countKey: "icd10CoveredPatientCount",
  },
  {
    key: "providerCoverageRate",
    label: "Provider coverage",
    definition:
      "Patients with a provider name in CareSpace treating/referring-provider fields, PAAF, recorded clinical visits (including future appointments), or linked CRM referrals.",
    countKey: "providerCoveredPatientCount",
  },
  {
    key: "npiCoverageRate",
    label: "NPI coverage",
    definition:
      "Patients with a checksum-valid clinician NPI in CareSpace, PAAF, recorded clinical visits, linked CRM referrals, or BCDA claims.",
    countKey: "npiCoveredPatientCount",
  },
  {
    key: "pverifyCoverageRate",
    label: "pVerify coverage",
    definition:
      "Patients with both an MBI and date of birth across patient records and linked CRM or BCDA data.",
    countKey: "pverifyCoveredPatientCount",
  },
  {
    key: "allDataCoverageRate",
    label: "All data",
    definition:
      "Patients with every required field: MBI, date of birth, phone, diagnosis code, provider, and provider NPI.",
    countKey: "allDataCoveredPatientCount",
  },
];

function coverageBackground(rate: number | null): string | undefined {
  if (rate === null || !Number.isFinite(rate)) return undefined;
  const clamped = Math.min(Math.max(rate, 0), 1);
  return `hsl(${Math.round(clamped * 120)} 70% 94%)`;
}

function sortableValue(clinic: DataCoverageClinic, key: string): string | number | null {
  if (key === "clinic") return clinic.name;
  if (key === "emr") return clinic.emrName;
  if (key === "eligiblePatientCount") return clinic.eligiblePatientCount;
  return clinic[key as keyof DataCoverageClinic] as number | null;
}

function CoverageMetricCell({
  clinic,
  metric,
}: {
  clinic: DataCoverageClinic;
  metric: CoverageMetric;
}) {
  const rate = clinic[metric.key];
  const coveredCount = clinic[metric.countKey];
  return (
    <td
      className={accountBodyCellClass({ numeric: true })}
      style={{ backgroundColor: coverageBackground(rate) }}
    >
      <span className="block font-semibold tabular-nums">{percent(rate, 1)}</span>
      <span className="block text-[11px] text-muted-foreground tabular-nums">
        {count(coveredCount)} / {count(clinic.eligiblePatientCount)}
      </span>
    </td>
  );
}

export function DataCoveragePanel({ data }: { data: DataCoverageResponse }) {
  const [clinicFilter, setClinicFilter] = useState("all");
  const [navigatorFilter, setNavigatorFilter] = useState("all");
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: "eligiblePatientCount",
    direction: "desc",
  });
  const portfolioTotal = data.clinics.find((clinic) => clinic.isPortfolioTotal);
  const clinics = useMemo(
    () => data.clinics.filter((clinic) => !clinic.isPortfolioTotal),
    [data.clinics],
  );
  const clinicOptions = useMemo(() => {
    const options = new Map<string, { id: string; name: string }>(
      clinics.map((clinic) => [clinic.id, { id: clinic.id, name: clinic.name }]),
    );
    data.pipeline?.clinics
      .filter((clinic) => !clinic.isPortfolioTotal)
      .forEach((clinic) => {
        if (!options.has(clinic.id)) {
          options.set(clinic.id, { id: clinic.id, name: clinic.name });
        }
      });
    return [...options.values()].sort((left, right) => left.name.localeCompare(right.name));
  }, [clinics, data.pipeline]);
  const navigatorOptions = useMemo(
    () =>
      [
        ...new Set(
          [...clinics, ...(data.pipeline?.clinics ?? [])].flatMap((clinic) =>
            navigatorNames(clinic.navigator),
          ),
        ),
      ].sort((left, right) => left.localeCompare(right)),
    [clinics, data.pipeline],
  );
  const visibleClinics = useMemo(() => {
    const filtered = clinics.filter(
      (clinic) =>
        (clinicFilter === "all" || clinic.id === clinicFilter) &&
        (navigatorFilter === "all" || navigatorNames(clinic.navigator).includes(navigatorFilter)),
    );
    return filtered.sort((left, right) =>
      compareNullableSortValues(
        sortableValue(left, sort.key),
        sortableValue(right, sort.key),
        sort.direction,
      ),
    );
  }, [clinicFilter, clinics, navigatorFilter, sort]);

  function updateSort(key: string) {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  }

  const headings = [
    {
      key: "clinic",
      label: "Clinic",
      definition: "The clinic name and the state where it is located.",
    },
    {
      key: "emr",
      label: "EMR / access",
      definition:
        "The clinic’s electronic medical record system and whether the team can access the EMR and its reports.",
    },
    {
      key: "eligiblePatientCount",
      label: "Eligible patients",
      definition:
        "Patients who are not archived and do not have a status that blocks GUIDE enrollment.",
    },
    ...COVERAGE_METRICS.map((metric) => ({
      key: metric.key,
      label: metric.label,
      definition: metric.definition,
    })),
  ];

  return (
    <section className="space-y-5 rounded-lg border border-border/70 bg-background/80 p-5 shadow-xs">
      <div>
        <AccountPanelHeading meta={`As of ${data.asOf}`}>Patient data coverage</AccountPanelHeading>
        <p className="mt-1 text-sm text-muted-foreground">
          Eligible patients are not archived and do not have an enrollment status that prevents
          GUIDE enrollment. Each coverage cell shows covered patients out of eligible patients.
        </p>
      </div>
      <DashboardFilterRow>
        <DashboardFilterField label="Clinic" htmlFor="account-management-coverage-clinic-filter">
          <DashboardSelect
            id="account-management-coverage-clinic-filter"
            value={clinicFilter}
            onChange={(event) => setClinicFilter(event.target.value)}
          >
            <option value="all">All clinics ({clinics.length})</option>
            {clinicOptions.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField
          label="Navigator"
          htmlFor="account-management-coverage-navigator-filter"
        >
          <DashboardSelect
            id="account-management-coverage-navigator-filter"
            value={navigatorFilter}
            onChange={(event) => setNavigatorFilter(event.target.value)}
          >
            <option value="all">All navigators</option>
            {navigatorOptions.map((navigator) => (
              <option key={navigator} value={navigator}>
                {navigator}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
      </DashboardFilterRow>
      <div>
        <h3 className="text-sm font-semibold">Patient data completeness</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Coverage combines structured data linked to each patient across CareSpaces, patient
          records, PAAF, clinical visits, CRM referrals, and BCDA. Recorded future appointments
          count. Separate from the new-referral pipeline below.
        </p>
      </div>
      <AccountTableFrame
        aria-label="Account Management data coverage"
        minWidthClass="min-w-[1550px]"
      >
        <thead>
          <tr>
            {headings.map((heading, index) => (
              <th
                key={heading.key}
                className={accountHeadCellClass(index === 0)}
                aria-sort={
                  sort.key === heading.key
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <span className="inline-flex items-center gap-1">
                  <SortHeading
                    column={heading.key}
                    label={heading.label}
                    sort={sort}
                    onSort={updateSort}
                  />
                  <ColumnHelp label={heading.label} definition={heading.definition} />
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...visibleClinics, ...(portfolioTotal ? [portfolioTotal] : [])].map((clinic) => (
            <tr
              key={clinic.id}
              className={clinic.isPortfolioTotal ? "bg-muted/60 font-semibold" : "group"}
            >
              <td className={accountBodyCellClass({ sticky: true })}>
                <span className="block truncate font-semibold" title={clinic.name}>
                  {clinic.name}
                </span>
                {!clinic.isPortfolioTotal ? (
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {clinic.state}
                  </span>
                ) : null}
              </td>
              <td className={accountBodyCellClass()}>
                {clinic.isPortfolioTotal ? null : (
                  <>
                    <span className="block font-medium">{clinic.emrName || "Not recorded"}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      EMR {clinic.emrAccess} · reports {clinic.reportAccess}
                    </span>
                  </>
                )}
              </td>
              <td className={accountBodyCellClass({ numeric: true })}>
                {count(clinic.eligiblePatientCount)}
              </td>
              {COVERAGE_METRICS.map((metric) => (
                <CoverageMetricCell key={metric.key} clinic={clinic} metric={metric} />
              ))}
            </tr>
          ))}
        </tbody>
      </AccountTableFrame>
      <p className="text-xs text-muted-foreground">
        Each field counts a patient once across the available linked sources. Phone numbers need at
        least 10 digits; CareSpace phone records must be active. Provider names must not be
        placeholders, and clinician NPIs must pass checksum validation. pVerify requires MBI and
        date of birth. All data requires MBI, date of birth, phone, ICD-10, provider, and NPI.
      </p>
      {data.pipeline ? (
        <PipelineCoveragePanel
          data={data.pipeline}
          clinicFilter={clinicFilter}
          navigatorFilter={navigatorFilter}
        />
      ) : null}
    </section>
  );
}
